import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import posthog from '../lib/posthog';
import { supabase } from '../lib/supabase';
import { fetchWorkspaceProfilesForUser } from '../lib/utils/fetch-workspace-profiles-for-user';

interface AuthState {
  user: WorkspaceUser | null;
  loading: boolean;
  setUser: (user: WorkspaceUser | null) => void;
  setCurrentWorkspace: (workspace: Workspace) => void;
  fetchWorkspaceProfiles: (user: User) => Promise<void>;
  signOut: () => Promise<void>;
}

export interface WorkspaceUser extends User {
  workspace_profiles?: Workspace[] | null | undefined;
  currentWorkspace?: Workspace | null;
}

export interface Workspace {
  workspace_id: string;
  role: { name: 'admin' | 'viewer' };
  workspaces?: {
    id: string;
    name: string;
    created_by: string;
    is_template: boolean;
    ps_token_balance: number;
    ps_token_total: number;
    ps_token_expiry: string | null;
    daily_ps_token_limit: number;
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  setUser: (user) => set({ user, loading: false }),
  setCurrentWorkspace: (workspace) => {
    if (!workspace) return;
    localStorage.setItem('currentWorkspace', JSON.stringify(workspace));

    set((state) => ({
      user: state.user ? { ...state.user, currentWorkspace: workspace } : null,
    }));
  },
  fetchWorkspaceProfiles: async (user) => {
    set({ loading: true });
    const result = await fetchWorkspaceProfilesForUser();

    const workspace_profiles = result?.workspaceProfiles ?? null;
    const isInternal = result?.isInternal ?? false;

    if (isInternal) {
      posthog.opt_out_capturing();
    } else {
      posthog.identify(user.id, { is_internal: false });
    }

    const savedWorkspace = localStorage.getItem('currentWorkspace');
    const persistedWorkspace = savedWorkspace
      ? JSON.parse(savedWorkspace)
      : null;

    const currentWorkspace =
      workspace_profiles?.find(
        (w) => w.workspace_id === persistedWorkspace?.workspace_id,
      ) ||
      workspace_profiles?.[0] ||
      null;

    set({
      user: { ...user, workspace_profiles, currentWorkspace },
      loading: false,
    });
  },
  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null, loading: false });
  },
}));
